import axios from 'axios';
import ReactGA from 'react-ga4';

import i18n from 'i18next';
import { useMediaQuery, useTheme } from '@mui/material';
import moment from './moment';
import { OrderDeliveryAddress } from '../store/features/order/orderTypes';
import { InfoType, PrintOption, ServiceCategory, ServiceOptions, UseFilterChangeParams } from './types';
import { FileInfo } from '../store/features/file/types';
import { AppDispatch, RootState } from '../store/configureStore';
import { BlogPost } from '../types/blog';
import { getBlogPosts } from '../data/blogPosts';

import { removeTeamMember } from '../store/features/team/teamServices';
import { setSnackbar } from '../store/features/app/appSlice';
import {
  SUPPORTED_LANGUAGES,
  formatCurrency,
  getLanguageConfig,
  getLocaleForLanguage,
  getTimezoneForLanguage,
} from './languageConfig';
import { italianShopIds } from './constants';

// Get blog post by slug, with translations automatically applied
export const findBlogPostBySlug = (slug: string): BlogPost | undefined => {
  const post = getBlogPosts().find((p) => p.slug === slug);

  if (!post) return undefined;

  return {
    ...post,
    title: i18n.t(`post.${slug}.title`, { defaultValue: post.title }),
    excerpt: i18n.t(`post.${slug}.excerpt`, { defaultValue: post.excerpt }),
    content: i18n.t(`post.${slug}.content`, { defaultValue: post.content }),
    author: i18n.t(`post.${slug}.author`, { defaultValue: post.author }),
    publishDate: i18n.t(`post.${slug}.publishDate`, { defaultValue: post.publishDate }),
  };
};

export const getStateAndToken = (thunkAPI: any): [RootState, string] => {
  const state: RootState = thunkAPI.getState() as RootState;
  const { access_token } = state.user.currentUser;
  return [state, access_token];
};

export const getFileIds = (files: FileInfo[] | null | undefined) => {
  if (!files || !Array.isArray(files)) {
    return [];
  }

  return files.map((file: FileInfo) => Number(file.id));
};

export const calculateTotalpagesPerFile = (files: Partial<FileInfo>[] | null | undefined) => {
  if (!files || !Array.isArray(files)) {
    return [];
  }

  return files.map((file: Partial<FileInfo>) => Number(file.number_of_pages || 0));
};

export const calculateTotalPages = (files: FileInfo[] | null | undefined) => {
  if (!files || !Array.isArray(files)) {
    return 0; // Return 0 if files is null, undefined or not an array
  }

  let numberOfPages = 0;

  files.forEach((file: any) => {
    numberOfPages += file.number_of_pages || 0; // Add 0 if file.number_of_pages is undefined
  });

  return numberOfPages === 1 ? 1 : numberOfPages;
};

export const getUrl = () => {
  const path = location.pathname;
  return stripLocaleFromPath(path);
};

// A file's size for people: "820 KB", "1.1 MB" (1 KB = 1,000 bytes, as phones and computers show it). Empty when
// it isn't known
export const formatFileSize = (bytes?: number | null) => {
  if (!bytes || bytes < 0) return '';
  if (bytes < 1000) return `${bytes} B`;
  if (bytes < 1000 * 1000) return `${Math.round(bytes / 1000)} KB`;
  const mb = bytes / (1000 * 1000);
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
};

export const useIsDesktop = () => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.up('md'));
};

export const getAvailableAndSelectedServices = (
  services: ServiceOptions[],
  currentUser: any,
  orderDeliveryAddress: any,
  service_type: string,
) => {
  if (!currentUser) {
    return { availableServices: [], selectedService: null };
  } else {
    const availableServices = services?.filter(
      (item: ServiceOptions) => item?.options[0]?.country === orderDeliveryAddress.country,
    );

    let selectedService: any = availableServices.find(
      (service) => service.name === service_type,
    )?.available_delivery_types;

    // BG deliveries: Remove "sameday" from available delivery types if city is not a valid destination
    const validSamedayDestinations = ['sofia', 'софия', 'sofiq', 'Sofia', 'София'];
    if (!validSamedayDestinations.includes(orderDeliveryAddress.city?.toLowerCase())) {
      selectedService = selectedService?.filter((item: any) => item.delivery_type_name !== 'sameday');
    }

    return { availableServices, selectedService };
  }
};

export const formatPrice = (country: string | undefined, price: number, shopId?: number) => {
  // Check if shopId is one of the Italian shops - prioritize this over country
  if (shopId && italianShopIds.includes(shopId)) {
    const value = price / 100;
    if (value <= 0) {
      return `€0.00`;
    }
    return `€${value.toFixed(2)}`;
  }

  // Handle delivery to Italy (when no shopId but country is IT)
  if (country === 'IT') {
    const value = price / 100;
    if (value <= 0) {
      return `€0.00`;
    }
    return `€${value.toFixed(2)}`;
  }

  if (country === 'BG') {
    // For Bulgaria, format using Bulgarian Leva in the current interface language
    const value = price / 100;
    if (value <= 0) {
      return `0.00 ${i18n.t('main.bgCurrency')}`;
    }
    return `${value.toFixed(2)} ${i18n.t('main.bgCurrency')}`;
  }

  // Default to UK formatting for other countries
  return formatCurrency(price, 'en', shopId);
};

export const handleFormatDate = (dateString: string): InfoType | undefined => {
  try {
    const targetDate = moment(dateString, 'YYYY-MM-DDTHH:mm:ss.SSSZ');
    const currentDate = moment().startOf('day');
    const tomorrowDate = moment().startOf('day').add(1, 'days');
    if (targetDate.isSame(currentDate, 'day')) {
      return { text: i18n.t('cards.today'), date: targetDate.format('DD.MM.YYYY') };
    } else if (targetDate.isSame(tomorrowDate, 'day')) {
      return { text: i18n.t('cards.tomorrow'), date: targetDate.format('DD.MM.YYYY') };
    } else {
      return { text: i18n.t('cards.onDate'), date: targetDate.format('DD.MM.YYYY') };
    }
  } catch (error) {
    null;
  }
};

export const formatServiceTime = (
  service_type: string,
  selectedService: any,
  delivery_type: string,
  selectedShop: any,
) => {
  if (service_type === 'delivery' && selectedService) {
    const filteredDeliveryType = selectedService.filter((obj: any) => obj.delivery_type_name === delivery_type);
    const deliveryDate = filteredDeliveryType.length > 0 ? filteredDeliveryType[0].delivery_date : moment();
    return moment(deliveryDate).locale(getLocaleForLanguage(i18n.language)).format('dddd, Do MMMM');
  } else if (service_type === 'click_and_collect' && selectedShop) {
    const config = getLanguageConfig(i18n.language);
    const timeSuffix = config.code === 'bg' ? 'ч' : 'h';
    const collectAtTime = moment(selectedShop?.collect_at);

    // Determine the timezone based on the shop - prioritize Italian shops
    let timezone = 'Europe/London'; // Default
    let timezoneKey = getTimezoneForLanguage(i18n.language);

    if (italianShopIds.includes(selectedShop.id)) {
      timezone = 'Europe/Rome';
      timezoneKey = 'europe_rome';
    } else if (selectedShop?.country === 'BG') {
      timezone = 'Europe/Sofia';
      timezoneKey = 'europe_sofia';
    }

    const countryTime = i18n.t(`timeZone.${timezoneKey}`);

    // Convert the time to the appropriate timezone
    const collectTime = collectAtTime.tz(timezone);

    // UK shops: "Tuesday 20 October, 14:57" (no "h" suffix or time zone; everyone ordering is in the UK)
    if (timezone === 'Europe/London') {
      return collectTime.locale('en').format('dddd D MMMM, HH:mm');
    }
    const formattedTime = collectTime.locale(i18n.language).format('dddd, Do MMMM, HH:mm');
    return formattedTime.charAt(0).toUpperCase() + formattedTime.slice(1) + timeSuffix + countryTime;
  }

  return null;
};

// Services
// Printt only operates in the UK, so the country is always the UK (this used to be an IP lookup)
export const getCountry = async () => 'UK';

export const handleAvailableServices = (
  services: ServiceOptions[],
  orderDeliveryAddress: OrderDeliveryAddress,
): ServiceOptions[] => {
  if (!orderDeliveryAddress.country) {
    return services;
  }

  const filteredServices = services?.filter(
    (item: ServiceOptions) => item?.options[0]?.country === orderDeliveryAddress.country,
  );

  return filteredServices;
};

export const arrayBufferToBase64 = (buffer: ArrayBuffer) => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

// Shop names from the API: keep "Mail Boxes" on one line (a line break between the two words reads badly)
export const shopName = (name: string | undefined) => (name ?? '').replace(/Mail Boxes/g, 'Mail\u00a0Boxes');

export const capitalizeOnlyFirstLetter = (string: string | undefined) => {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

export const useFilterChange = ({
  setActiveClass,
  setFilterValue,
  setDate,
  tableData,
  setOrderQuery,
}: UseFilterChangeParams) => {
  return (input: any) => {
    // Determine if the input is an event object or a direct value
    const isEvent = input && input.target;
    const selectedFilter = isEvent ? input.target.value : input;

    // Apply the logic based on the selectedFilter
    setActiveClass(selectedFilter);
    setFilterValue(selectedFilter);

    switch (selectedFilter) {
      case 'today':
        setDate({
          startDate: moment().startOf('day').toDate(),
          endDate: moment().endOf('day').toDate(),
          key: 'selection',
        });
        if (setOrderQuery) setOrderQuery('today');
        break;
      case 'last7days':
        setDate({
          startDate: moment().subtract(6, 'days').toDate(),
          endDate: moment().toDate(),
          key: 'selection',
        });
        if (setOrderQuery) setOrderQuery('week');
        break;
      case 'last4weeks':
        setDate({
          startDate: moment().subtract(4, 'weeks').toDate(),
          endDate: moment().toDate(),
          key: 'selection',
        });
        if (setOrderQuery) setOrderQuery('month');
        break;
      // 30 days, matching the API's "month" (orders/utils.py: month = 30 days)
      case 'last30days':
        setDate({
          startDate: moment().subtract(30, 'days').toDate(),
          endDate: moment().toDate(),
          key: 'selection',
        });
        if (setOrderQuery) setOrderQuery('month');
        break;
      case 'last3months':
        setDate({
          startDate: moment().subtract(3, 'months').toDate(),
          endDate: moment().toDate(),
          key: 'selection',
        });
        if (setOrderQuery) setOrderQuery('quarter');
        break;
      case 'last12months':
        setDate({
          startDate: moment().subtract(12, 'months').toDate(),
          endDate: moment().toDate(),
          key: 'selection',
        });
        if (setOrderQuery) setOrderQuery('year');
        break;
      case 'allTime':
        setDate({
          startDate: moment('2000-01-01').toDate(),
          endDate: moment().toDate(),
          key: 'selection',
        });
        if (setOrderQuery) setOrderQuery('all');
        break;
      default:
        tableData;
        break;
    }
  };
};

const range = (startAt: number, size: number): Array<number> => {
  return [...Array(size - startAt).keys()].map((i) => i + startAt);
};
const getBindingOption = (sheetNumber: number): string => {
  const bindingOptionToSheetRangeMap = {
    office1_binding_70: range(1, 70),
    office1_binding_70_165: range(70, 165),
    office1_binding_165_240: range(165, 240),
    office1_binding_240_330: range(240, 330),
    office1_binding_330_450: range(330, 451),
  };

  for (const [option, sheetRange] of Object.entries(bindingOptionToSheetRangeMap)) {
    if (sheetRange.includes(sheetNumber)) {
      return option;
    }
  }
  return '';
};

export const calculateBindingPriceBg = (
  documents: Array<FileInfo>,
  copies: number,
  is_duplex: boolean,
  serviceOptions: Array<PrintOption>,
) => {
  let bindingOptionName = 'office1_binding_70',
    bindingPrice = 0,
    sheetNumber = 1;

  documents.forEach((document: FileInfo) => {
    let documentBindingPrice = 0;

    if (is_duplex) {
      sheetNumber = Math.round(document.number_of_pages / 2);
    } else {
      sheetNumber = document.number_of_pages;
    }

    bindingOptionName = getBindingOption(sheetNumber);

    serviceOptions.forEach((item: PrintOption) => {
      if (item.name === bindingOptionName) {
        documentBindingPrice = calculateStartingPrice(documents.length * copies, item.price_categories);
      }
    });
    bindingPrice += documentBindingPrice;
  });

  return bindingPrice;
};

export const calculateSheetNumber = (uploadedFiles: Array<FileInfo>, isDuplex: boolean) => {
  let sheetNumber = 0;
  uploadedFiles.forEach((file: FileInfo) => {
    const filePages = isDuplex ? Math.ceil(file.number_of_pages / 2) : file.number_of_pages;
    sheetNumber += filePages;
  });
  return sheetNumber;
};
export const calculateStartingPrice = (totalPages: number, range: Array<ServiceCategory> | undefined | number) => {
  let price: any = 0;
  if (range && typeof range !== 'number') {
    range.forEach((x: ServiceCategory) => {
      if ((totalPages >= x.start && totalPages < x.end) || (totalPages >= x.start && x.end === null)) {
        price = x.price;
      }
    });
  } else {
    price = range;
  }
  return price;
};
export const TrackGoogleAnalyticsEvent = (event_name: any, data: any) => {
  // Push data to the data layer
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: event_name,
    ...data,
  });

  // Send GA4 Event
  ReactGA.event(event_name, data);
};

// Shiping Cards

export const getDeliveryTypeLabel = (type: string, orderDeliveryAddress: any) => {
  const language = i18n.language;

  if (orderDeliveryAddress?.country === 'BG') {
    if (orderDeliveryAddress?.city === 'Sofia') {
      if (type === 'sameday') {
        return language === 'bg' ? 'Експресна' : 'Express';
      } else if (type === 'standard') {
        return language === 'bg' ? 'Стандартна' : 'Standard';
      } else if (type === 'nextday') {
        return language === 'bg' ? 'Стандартна' : 'Standard';
      }
    } else {
      if (type === 'sameday') {
        return language === 'bg' ? 'Експресна' : 'Express';
      } else if (type === 'nextday') {
        return language === 'bg' ? 'Стандартна' : 'Standard';
      }
    }
  } else if (orderDeliveryAddress?.country === 'UK') {
    if (type === 'nextday') {
      return language === 'bg' ? 'Експресна' : 'Express';
    } else if (type === 'tracked') {
      return language === 'bg' ? 'Проследена' : 'Tracked';
    }
    if (type === 'sameday') {
      return language === 'bg' ? 'Експресна' : 'Express';
    } else if (type === 'standard') {
      return language === 'bg' ? 'Стандартна' : 'Standard';
    }
  } else if (orderDeliveryAddress?.country === 'IT') {
    // Add Italian delivery type labels
    if (type === 'nextday') {
      return language === 'it' ? 'Express' : 'Express';
    } else if (type === 'standard') {
      return language === 'it' ? 'Standard' : 'Standard';
    }
  }
  return capitalizeOnlyFirstLetter(type);
};

export const getDeliveryTime = (type: string, orderDeliveryAddress: any) => {
  const language = i18n.language;

  if (orderDeliveryAddress?.country === 'BG') {
    if (orderDeliveryAddress?.city === 'Sofia') {
      if (type === 'sameday') {
        return language === 'bg' ? '1 - 2 Дни' : '1 - 2 Days';
      } else if (type === 'standard') {
        return language === 'bg' ? '1 - 4 Дни' : '1 - 4 Days';
      }
    } else {
      if (type === 'sameday') {
        return language === 'bg' ? '1 -2 Дни' : '1 - 2 Days';
      } else if (type === 'standard') {
        return language === 'bg' ? '1 - 4 Дни' : '1 - 4 Days';
      }
    }
  } else if (orderDeliveryAddress?.country === 'UK') {
    // Matches the API's own arrival estimate: Express (nextday) 1 working day, Standard/Tracked 2 (3 with
    // binding or lamination), each +1 after the daily cut-off
    if (type === 'nextday') {
      return language === 'bg' ? '1 - 2 Дни' : '1–2 working days';
    } else if (type === 'tracked' || type === 'standard') {
      return language === 'bg' ? '1 - 4 Дни' : '2–4 working days';
    }
  } else if (orderDeliveryAddress?.country === 'IT') {
    // Add Italian delivery times
    if (type === 'nextday') {
      return language === 'it' ? '1 - 2 Giorni' : '1 - 2 Days';
    } else if (type === 'standard') {
      return language === 'it' ? '1 - 4 Giorni' : '1 - 4 Days';
    }
  }

  // Default fallback
  if (language === 'bg') return '1 - 4 Дни';
  if (language === 'it') return '1 - 4 Giorni';
  return '2–4 working days';
};

export const getDeliveryFee = (fee: number, orderDeliveryAddress: any, shopId?: number) => {
  const language = i18n.language;
  // "Free", as the order summary says, otherwise the fee in full ("+ £3.50", not "+ £3.5")
  if (fee === 0) return i18n.t('orderPage.free');
  const amount = (fee / 100).toFixed(2);

  // Check if shopId is one of the Italian shops - prioritize this over country
  if (shopId && italianShopIds.includes(shopId)) {
    return `+ €${amount}`;
  }

  if (orderDeliveryAddress?.country === 'BG') {
    return language === 'bg' ? `+ ${amount} лв.` : `+ ${amount} lv.`;
  } else if (orderDeliveryAddress?.country === 'IT') {
    return `+ €${amount}`;
  }
  return `+ £${amount}`;
};

export const isHomePage = (url: string) => {
  return (
    url === '/' || url === '/en' || url === '/bg' || url === '/it' || url === '/en/' || url === '/bg/' || url === '/it/'
  );
};

export const isInstantOrderPage = (url: string) => {
  return url === '/instantOrder' || url.endsWith('/instantOrder');
};

export const isAuthPage = (url: string) => {
  return url === '/auth' || url.endsWith('/auth');
};

export const stripLocaleFromPath = (path: string) => {
  return path.replace(/^\/[a-z]{2}\//, '/');
};

/**
 * Modifies HTML string to make all links open in a new tab
 * @param htmlString Original HTML content
 * @returns Modified HTML with links that open in new tabs
 */
export const addTargetBlankToLinks = (htmlString: string): string => {
  // Create a temporary element to parse the HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlString;

  // Find all links in the content
  const links = tempDiv.querySelectorAll('a');

  // Add target="_blank" and rel="noopener noreferrer" to all links
  links.forEach((link) => {
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener noreferrer');
  });

  // Return the modified HTML
  return tempDiv.innerHTML;
};

export const getStatusCategories = (t: any) => {
  return {
    successStatuses: [
      t('orders.statuses.success'),
      t('orders.statuses.delivered'),
      t('teams.confirmed'),
      t('billing.paid'),
    ],
    failStatuses: [
      t('orders.statuses.canceled'),
      t('orders.statuses.failed'),
      t('orders.statuses.rejected'),
      t('orders.statuses.refunded'),
    ],
    inProcessStatuses: [
      t('orders.statuses.printing'),
      t('orders.statuses.downloading'),
      t('orders.statuses.delivering'),
    ],
  };
};

/**
 * Handle team member removal with proper error handling
 */
export const handleRemoveTeamMember = async (row: any, dispatch: AppDispatch, t: any, onSuccess?: () => void) => {
  try {
    const res = await dispatch(removeTeamMember(row.id));
    if (res.meta.requestStatus === 'fulfilled') {
      dispatch(setSnackbar({ open: true, message: t('teams.memberRemoved'), type: 'success' }));
      if (onSuccess) onSuccess();
    } else {
      alert(t('error.wentWrong'));
    }
  } catch (error) {
    console.error('Error removing team member:', error);
    alert(t('error.wentWrong'));
  }
};

/**
 * Get the status class name based on the status value
 */
export const getStatusClassName = (
  column: string,
  value: string,
  statusLabel: string,
  classes: any,
  statusCategories: ReturnType<typeof getStatusCategories>,
) => {
  if (column !== statusLabel) return '';

  const { successStatuses, failStatuses, inProcessStatuses } = statusCategories;

  if (successStatuses.includes(value?.toLowerCase())) {
    return classes.successStatus;
  } else if (failStatuses.includes(value?.toLowerCase())) {
    return classes.cancelStatus;
  } else if (inProcessStatuses.includes(value?.toLowerCase())) {
    return classes.receiveStatus;
  } else {
    return classes.pendingStatus;
  }
};

// Add this new function to help with locale-aware navigation
export const ensureLocaleInPath = (path: string, locale?: string): string => {
  // Use provided locale or current i18n language, fallback to 'en'
  const targetLocale =
    locale || (i18n.language && SUPPORTED_LANGUAGES.includes(i18n.language as any) ? i18n.language : 'en');

  const pathSegments = path.split('/').filter((segment) => segment !== '');
  const supportedLanguages = ['en', 'bg', 'it']; // Import from languageConfig if needed

  // Check if first segment is a supported language
  const hasLocalePrefix = pathSegments.length > 0 && supportedLanguages.includes(pathSegments[0]);

  if (hasLocalePrefix) {
    // Replace existing locale with target locale
    pathSegments[0] = targetLocale;
    return '/' + pathSegments.join('/');
  } else {
    // Add locale to beginning
    return path === '/' ? `/${targetLocale}` : `/${targetLocale}${path}`;
  }
};
